import Input from "./common/Input";
import SelectBox from "./common/SelectBox";
import { useMorningAuthStore } from "../../../../store/useMorningAuthStore";
import { useGetDepartments } from "../../../../hooks/morning/useGetDept";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useMasterAuthStore } from "../../../../store/useMasterAuthStore";

const ReceiptInfo = forwardRef(
    (
        {
            modify = false,
            data,
            setData,
            id,
            master,
            errorMessages,
            isSubmitting,
        }: { modify?: boolean; data: any; setData: Function; id?: string; master?: boolean; errorMessages?: any; isSubmitting?: boolean },
        ref,
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const c_code = useMorningAuthStore(state => state.c_code ?? "");
        const c_company = useMorningAuthStore(state => state.c_company);
        const c_req_dept = useMorningAuthStore(state => state.c_req_dept);
        const masterCCompany = useMasterAuthStore(state => state.c_company);
        const masterStoreCCode = useMasterAuthStore(state => state.c_code ?? "");

        const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

        const { data: deptData } = useGetDepartments({ master });
        const handleChange = (key: string, value: any) => {
            if (key === "tel") {
                const onlyNumbers = value.replace(/\D/g, "").slice(0, 11);
                let formatted = "";

                if (onlyNumbers.length < 4) {
                    formatted = onlyNumbers;
                } else if (onlyNumbers.length < 8) {
                    formatted = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3)}`;
                } else {
                    formatted = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3, 7)}-${onlyNumbers.slice(7)}`;
                }

                setData((prev: any) => ({ ...prev, [key]: formatted }));

                const isInvalid = isSubmitting && formatted.length !== 13; // 010-1234-5678
                setErrors((prev: any) => ({ ...prev, [key]: isInvalid }));
                return;
            }
            setData((prev: any) => ({ ...prev, [key]: value }));

            if (isSubmitting) {
                let isInvalid = false;
                if (!value.trim()) isInvalid = true;

                // 담당자명은 한글 1개 이상 필수
                if (key === "manager") {
                    const hasKorean = /[가-힣]/.test(value);
                    if (!value.trim() || !hasKorean) isInvalid = true;
                }

                if (key === "departmentName" || key === "department") {
                    setErrors(prev => ({ ...prev, department: isInvalid }));
                } else {
                    setErrors(prev => ({ ...prev, [key]: isInvalid }));
                }
            } else {
                if (key === "departmentName" || key === "department") {
                    setErrors(prev => ({ ...prev, department: false }));
                } else {
                    setErrors(prev => ({ ...prev, [key]: false }));
                }
            }
        };

        useImperativeHandle(ref, () => ({
            validate: () => {
                const newErrors: { [key: string]: boolean } = {};
                const requiredFields = ["tel", "manager"];
                if (!c_code) {
                    requiredFields.push("name");
                }
                if (c_req_dept === "Y" && ((c_code && deptData?.length) || -1 > 0)) {
                    if (!data.department?.trim()) {
                        newErrors.department = true;
                    }
                } else if (c_req_dept === "Y") {
                    if (!data.departmentName?.trim()) {
                        newErrors.department = true;
                    }
                }
                requiredFields.forEach(field => {
                    const value = data[field];
                    if (typeof value === "string") {
                        if (!value.trim()) {
                            newErrors[field] = true;
                        } else if (field === "manager") {
                            // 담당자명은 한글 1개 이상 필수
                            const hasKorean = /[가-힣]/.test(value);
                            if (!hasKorean) newErrors[field] = true;
                        }
                    } else if (value === undefined || value === null) {
                        newErrors[field] = true;
                    }
                });

                setErrors(newErrors);
                return Object.keys(newErrors).length === 0;
            },
            scrollIntoView: () => {
                containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            },
        }));

        return (
            <div
                ref={containerRef}
                id={id}
                className={`bg-white ${modify ? "w-full max-w-[680px]" : "w-full"} rounded-2xl p-10 responsive1200:p-[16px]`}
            >
                <div className="border-b-[2px] text-[28px] font-bold border-black flex items-center pb-2 responsive1200:text-[20px]">
                    접수자 정보
                </div>
                <div className={`${modify ? "flex flex-col" : "flex gap-[40px] responsive1200:flex-col responsive1200:gap-0"}`}>
                    <div className="flex-1">
                        <div className="flex items-center py-[33px] responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                            <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                상호명 <span className="text-new text-[16px] font-normal">*</span>
                            </div>
                            {(!master && c_code) || (master && masterStoreCCode) ? (
                                <p className="text-[18px] font-[500] text-[#1B1C1E]">{masterCCompany || c_company}</p>
                            ) : (
                                <div className={`flex flex-col gap-2 ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                    <Input
                                        placeholder="상호명을 입력해주세요."
                                        className="w-full"
                                        value={data.name}
                                        onChange={e => handleChange("name", e.target.value)}
                                        error={errors?.name}
                                    />
                                    {errors?.name && <p className="text-[15px] text-[#ff4242]">{errorMessages?.name}</p>}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center mb-7 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                            <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                휴대폰 번호 <span className="text-new text-[16px] font-normal">*</span>
                            </div>
                            <div className={`flex flex-col gap-2 ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                <Input
                                    maxLength={13}
                                    placeholder="연락처를 입력해주세요."
                                    className="w-full"
                                    value={data.tel}
                                    onChange={e => handleChange("tel", e.target.value)}
                                    error={errors?.tel}
                                />
                                {errors?.tel && <p className="text-[15px] text-[#ff4242]">{errorMessages?.tel}</p>}
                            </div>
                        </div>
                        <div className="flex items-center mb-7 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                            <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                부서명 {c_req_dept === "Y" && <span className="text-new text-[16px] font-normal">*</span>}
                            </div>
                            {c_code && deptData && deptData.length > 0 ? (
                                <SelectBox
                                    className={`${modify ? "w-full max-w-[440px]" : "w-full"}`}
                                    placeholder="부서선택 또는 직접입력 후 선택하세요."
                                    searchable
                                    options={
                                        deptData?.map(dept => ({
                                            value: dept.sn.toString(),
                                            label: dept.dept_name,
                                        })) || []
                                    }
                                    value={data.department}
                                    onChange={(value: string) => {
                                        const selected = deptData?.find(dept => dept.sn.toString() === value);
                                        handleChange("department", value);
                                        handleChange("departmentName", selected?.dept_name || "");
                                    }}
                                    error={errors?.department}
                                />
                            ) : (
                                <div className={`flex flex-col gap-2 ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                    <Input
                                        placeholder="부서명을 입력해주세요."
                                        className="w-full"
                                        value={data.departmentName}
                                        onChange={e => handleChange("departmentName", e.target.value)}
                                        error={errors?.department}
                                    />
                                    {errors?.department && <p className="text-[15px] text-[#ff4242]">{errorMessages?.departmentName}</p>}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center mb-7 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                            <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                담당자명 <span className="text-new text-[16px] font-normal">*</span>
                            </div>
                            <div className={`flex flex-col gap-2 ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                <Input
                                    placeholder="담당자명을 입력해주세요."
                                    className="w-full"
                                    value={data.manager}
                                    onChange={e => handleChange("manager", e.target.value)}
                                    error={errors?.manager}
                                />
                                {errors?.manager && <p className="text-[15px] text-[#ff4242]">{errorMessages?.manager}</p>}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1">
                        <div className="flex items-start py-[33px] responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                            <div className="text-[18px] font-[500] min-w-[160px] mt-2">픽업시 특이사항</div>
                            <textarea
                                placeholder="특이사항을 입력해주세요."
                                className={`${modify ? "w-full max-w-[440px]" : "w-full"} h-[140px] focus:border-2 focus:border-new resize-none border rounded-lg p-[15px] outline-0 text-[18px]`}
                                value={data.pickupMessage}
                                onChange={e => handleChange("pickupMessage", e.target.value)}
                            />
                        </div>
                        <div className="flex items-start responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                            <div className="text-[18px] font-[500] min-w-[160px] mt-2">
                                배송시 특이사항
                                <br />
                                (메모)
                            </div>
                            <textarea
                                placeholder="요청사항을 입력해주세요."
                                className={`${modify ? "w-full max-w-[440px]" : "w-full"} h-[140px] focus:border-2 focus:border-new resize-none border rounded-lg p-[15px] outline-0 text-[18px]`}
                                value={data.deliveryMessage}
                                onChange={e => handleChange("deliveryMessage", e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export default ReceiptInfo;
